import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import EDateText from '@/components/everridge/EDateText';
import EToast from '@/components/everridge/EToast';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import JsonPayloadEditor from './JsonPayloadEditor';
import {
    useIntegrationMessageDetailApi,
    useIntegrationMessageMutations,
} from './useIntegrationMessagesApi';
import type {
    EmailOutboxMessageDetailDto,
    InboxMessageDetailDto,
    IntegrationMessageKind,
    InternalOutboxMessageDetailDto,
    OutboxMessageDetailDto,
} from './integration-message.types';
import { getIntegrationMessageBasePath, hasAggregateFields, hasExternalIdField, hasTenantField, hasTryCountField } from './integration-message.utils';

type Props = {
    kind: IntegrationMessageKind;
};

const IntegrationMessageDetailPage = ({ kind }: Props) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userRole = useUserRole();

    const { message, isLoading } = useIntegrationMessageDetailApi(kind, id);
    const { updateMutation, deleteMutation } = useIntegrationMessageMutations(kind);

    const [externalId, setExternalId] = useState('');
    const [type, setType] = useState('');
    const [aggregateType, setAggregateType] = useState('');
    const [aggregateId, setAggregateId] = useState('');
    const [aggregateVersion, setAggregateVersion] = useState('0');
    const [payload, setPayload] = useState('{}');
    const [isProcessed, setIsProcessed] = useState(false);
    const [error, setError] = useState('');
    const [tryCount, setTryCount] = useState('0');

    useEffect(() => {
        if (!message) return;

        setType(message.type);
        setPayload(message.payload ?? '{}');
        setIsProcessed(message.isProcessed);
        setError(message.error ?? '');

        if (kind === 'inbox') {
            const inbox = message as InboxMessageDetailDto;
            setExternalId(inbox.externalId ?? '');
            setAggregateType(inbox.aggregateType);
            setAggregateId(inbox.aggregateId);
            setAggregateVersion(String(inbox.aggregateVersion));
        } else if (kind === 'outbox') {
            const outbox = message as OutboxMessageDetailDto;
            setAggregateType(outbox.aggregateType);
            setAggregateId(outbox.aggregateId);
            setAggregateVersion(String(outbox.aggregateVersion));
        } else if (hasTryCountField(kind)) {
            const retryable = message as InternalOutboxMessageDetailDto | EmailOutboxMessageDetailDto;
            setTryCount(String(retryable.tryCount));
        }
    }, [message, kind]);

    const payloadError = useMemo(() => {
        if (!payload.trim()) return kind === 'inbox' ? null : t('integrationMessages.payload.required');
        try {
            JSON.parse(payload);
            return null;
        } catch {
            return t('integrationMessages.payload.invalid');
        }
    }, [payload, kind, t]);

    if (userRole !== Roles.superAdmin) return null;

    const goBack = () => navigate(getIntegrationMessageBasePath(kind));

    const titleKey =
        kind === 'inbox'
            ? 'integrationMessages.inbox.detailTitle'
            : kind === 'outbox'
              ? 'integrationMessages.outbox.detailTitle'
              : kind === 'emailOutbox'
                ? 'integrationMessages.emailOutbox.detailTitle'
              : 'integrationMessages.internalOutbox.detailTitle';

    const handleSave = async () => {
        if (!id || payloadError) return;

        if (kind === 'inbox') {
            await updateMutation.mutateAsync({
                id,
                body: {
                    externalId: externalId || null,
                    type,
                    aggregateType,
                    aggregateId,
                    aggregateVersion: Number(aggregateVersion),
                    payload: payload.trim() ? payload : null,
                    isProcessed,
                    error: error || null,
                },
            });
        } else if (kind === 'outbox') {
            await updateMutation.mutateAsync({
                id,
                body: {
                    type,
                    aggregateType,
                    aggregateId,
                    aggregateVersion: Number(aggregateVersion),
                    payload,
                    isProcessed,
                    error: error || null,
                },
            });
        } else {
            await updateMutation.mutateAsync({
                id,
                body: {
                    type,
                    payload,
                    isProcessed,
                    error: error || null,
                    tryCount: Number(tryCount),
                },
            });
        }

        EToast({ variant: 'success', message: t('integrationMessages.toast.saved') });
        goBack();
    };

    const handleDelete = async () => {
        if (!id) return;
        await deleteMutation.mutateAsync(id);
        EToast({ variant: 'success', message: t('integrationMessages.toast.deleted') });
        goBack();
    };

    if (isLoading || !message) {
        return (
            <div className="flex flex-1 items-center justify-center">
                <Loader2 className="size-6 animate-spin text-gray" />
            </div>
        );
    }

    return (
        <section className="flex flex-1 flex-col gap-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-black">{t(titleKey)}</h1>
                    <p className="text-sm text-gray">{message.id}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={goBack}>
                        {t('integrationMessages.actions.back')}
                    </Button>
                    <Button variant="destructive" onClick={() => void handleDelete()}>
                        {t('integrationMessages.actions.delete')}
                    </Button>
                    <Button onClick={() => void handleSave()} disabled={Boolean(payloadError)}>
                        {t('integrationMessages.actions.save')}
                    </Button>
                </div>
            </div>

            <div className="grid gap-4 rounded-semi-xl border border-lightgray bg-base p-4 md:grid-cols-2">
                {hasTenantField(kind) && (
                    <div>
                        <p className="text-xs uppercase text-gray">{t('integrationMessages.detail.tenant')}</p>
                        <p className="text-sm font-medium">
                            {(message as InboxMessageDetailDto | OutboxMessageDetailDto | InternalOutboxMessageDetailDto).tenantName ?? '—'}
                        </p>
                        <p className="text-xs text-gray">
                            {(message as InboxMessageDetailDto | OutboxMessageDetailDto | InternalOutboxMessageDetailDto).tenantId}
                        </p>
                    </div>
                )}
                <div>
                    <p className="text-xs uppercase text-gray">
                        {kind === 'inbox'
                            ? t('integrationMessages.table.receivedAt')
                            : t('integrationMessages.table.occurredAt')}
                    </p>
                    <p className="text-sm">
                        <EDateText
                            date={
                                new Date(
                                    kind === 'inbox'
                                        ? (message as InboxMessageDetailDto).receivedAt
                                        : (message as OutboxMessageDetailDto | InternalOutboxMessageDetailDto | EmailOutboxMessageDetailDto)
                                              .occurredAt,
                                )
                            }
                        />
                    </p>
                </div>
                {message.processedAt && (
                    <div>
                        <p className="text-xs uppercase text-gray">{t('integrationMessages.detail.processedAt')}</p>
                        <p className="text-sm">
                            <EDateText date={new Date(message.processedAt)} />
                        </p>
                    </div>
                )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm">
                    <span>{t('integrationMessages.detail.type')}</span>
                    <input
                        value={type}
                        onChange={(event) => setType(event.target.value)}
                        className="rounded-lg border border-lightgray bg-base px-3 py-2"
                    />
                </label>

                {hasExternalIdField(kind) && (
                    <label className="flex flex-col gap-1 text-sm">
                        <span>{t('integrationMessages.table.externalId')}</span>
                        <input
                            value={externalId}
                            onChange={(event) => setExternalId(event.target.value)}
                            className="rounded-lg border border-lightgray bg-base px-3 py-2"
                        />
                    </label>
                )}

                {hasAggregateFields(kind) && (
                    <>
                        <label className="flex flex-col gap-1 text-sm">
                            <span>{t('integrationMessages.detail.aggregateType')}</span>
                            <input
                                value={aggregateType}
                                onChange={(event) => setAggregateType(event.target.value)}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                            <span>{t('integrationMessages.filters.aggregateId')}</span>
                            <input
                                value={aggregateId}
                                onChange={(event) => setAggregateId(event.target.value)}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                            <span>{t('integrationMessages.detail.aggregateVersion')}</span>
                            <input
                                type="number"
                                value={aggregateVersion}
                                onChange={(event) => setAggregateVersion(event.target.value)}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2"
                            />
                        </label>
                    </>
                )}

                {hasTryCountField(kind) && (
                    <label className="flex flex-col gap-1 text-sm">
                        <span>{t('integrationMessages.table.tryCount')}</span>
                        <input
                            type="number"
                            min={0}
                            value={tryCount}
                            onChange={(event) => setTryCount(event.target.value)}
                            className="rounded-lg border border-lightgray bg-base px-3 py-2"
                        />
                    </label>
                )}

                <label className="flex items-center gap-2 text-sm md:col-span-2">
                    <input
                        type="checkbox"
                        checked={isProcessed}
                        onChange={(event) => setIsProcessed(event.target.checked)}
                    />
                    <span>{t('integrationMessages.detail.isProcessed')}</span>
                </label>

                <label className="flex flex-col gap-1 text-sm md:col-span-2">
                    <span>{t('integrationMessages.table.error')}</span>
                    <textarea
                        value={error}
                        onChange={(event) => setError(event.target.value)}
                        rows={3}
                        className="rounded-lg border border-lightgray bg-base px-3 py-2"
                    />
                </label>
            </div>

            <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">{t('integrationMessages.detail.payload')}</p>
                <JsonPayloadEditor value={payload} onChange={setPayload} />
            </div>
        </section>
    );
};

export default IntegrationMessageDetailPage;
